import React, { useState, useEffect } from 'react';
import { MenuItem, Box, Menu, InputAdornment, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useToast } from 'use-toast-mui';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';

import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputPrice } from '../../../../form-components/FormInputPrice';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import AccessDoorSelect from '../../common/AccessDoorSelect';
import BranchSelectModal from '../../../common/modal/BranchSelectModal';

import {
  CardsContainer,
  InfoCard,
  InfoCardTitle,
  InfoCardHeader,
  InfoCardHeaderRow,
  InfoCardHint,
  BasicInfoColumn,
  FieldGroup,
  FieldLabel,
  FieldLabelRow,
  FieldLabelWithInfo,
  FieldHint,
  InputUnit,
  HalfWidthContainer,
  HalfWidthField,
  RadioGroupRow,
  RadioOption,
  RadioCircleNew,
  RadioDotNew,
  RadioLabelNew,
  DayButtonsRow,
  DayButtonNew,
  TimePickerContainer,
  TimePickerGroup,
  TimeSelectSmall,
  TimeColon,
  TimeTilde,
  DatePickerRow,
  DatePickerField,
  CheckboxRow,
  CheckboxNew,
  CheckboxLabelNew,
  ToggleContainer,
  ToggleLabel,
  StyledSwitch,
  BottomActionsNew,
  CancelButtonNew,
  NextButtonNew,
  ErrorFieldWrapper,
  TimeSelect,
  GrayInputWrapper,
  DiscountUnitSelect,
  InfoIconSmall,
  SportTagPlaceholder,
  UnitInputCombo,
  UnitInputField,
  UnitDropdownButton,
  UnitFixedText,
} from './PassEditStyled';
import SportTypeSelect from '../../../common/SportTypeSelect';

function PassEditStep01({
  control,
  watch,
  setValue,
  getValues,
  user,
  data,
  selectedDays,
  handleDayToggle,
  timeSettings,
  handleTimeChange,
  usagePeriodType = 'MONTH',
  setUsagePeriodType,
  isTaxFree,
  setIsTaxFree,
  discountType,
  setDiscountType,
  hasDiscountEndDate,
  setHasDiscountEndDate,
  handleRegularPriceChange,
  handleDiscountRateChange,
  handleFinalPriceChange,
  onNext,
  onCancel,
  loader,
  DAYS,
  HOURS,
  MINUTES,
  centrerInfo,
  appExposure,
  onExposureToggle,
  usageType,
  setUsageType,
  productType,
  setProductType,
  isEditMode,
  selectedDoorIds,
  onDoorIdsChange,
  onDoorConvertedChange,
  copySourceName = '',
  branchOptions = [],
  legacyBranchNames = {},
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [errorFields, setErrorFields] = useState({});

  // 판매·이용지점 선택 모달 상태 (#73 판매/이용 분리 — 지점이 많아도 검색·전체선택 가능한 모달)
  const [branchModalOpen, setBranchModalOpen] = useState(false);
  const [branchMenuField, setBranchMenuField] = useState(null); // 'saleBranchIds' | 'useBranchIds'
  const [sameAsSale, setSameAsSale] = useState(false);
  // 이용지점=구매한 지점(소속지점) 모드(#98) — 켜면 이용지점 목록 대신 회원이 결제한 지점 1곳만 이용
  const useSaleBranch = watch('useSaleBranchYn') === 'Y';
  const useBranchLocked = sameAsSale || useSaleBranch;
  const toggleUseSaleBranch = () => {
    const next = !useSaleBranch;
    setValue('useSaleBranchYn', next ? 'Y' : 'N');
    if (next) {
      setSameAsSale(false);
      setValue('useBranchIds', []);
    } else if (!(watch('useBranchIds') || []).length) {
      setValue('useBranchIds', watch('saleBranchIds') || []);
    }
  };
  const toggleSameAsSale = () => {
    if (!sameAsSale) setValue('useSaleBranchYn', 'N');
    setSameAsSale(!sameAsSale);
  };
  const myBranchIsDirect = branchOptions.some(option => option.branchId === user?.branch?.branchId);
  const isMultiBranch = branchOptions.length > 1 && myBranchIsDirect;
  const branchNameOf = branchId =>
    branchOptions.find(option => option.branchId === branchId)?.branchName
      ?? legacyBranchNames[branchId]
      ?? (branchId === user?.branch?.branchId ? user?.branch?.branchName : `지점 #${branchId}`);

  const branchFieldValue = field => (field ? watch(field) : []) || [];

  // 선택 요약 — 1개면 지점명 그대로, 여러 개면 "OO 외 N개"
  const branchSummaryText = field => {
    const ids = branchFieldValue(field);
    if (ids.length === 0) return null;
    if (ids.length === 1) return branchNameOf(ids[0]);
    return `${branchNameOf(ids[0])} 외 ${ids.length - 1}개`;
  };

  const openBranchModal = field => {
    setBranchMenuField(field);
    setBranchModalOpen(true);
  };

  // 이용지점 "판매지점과 동일" — 체크되어 있는 동안 판매지점 변경을 그대로 따라간다
  const sameAsSaleSourceKey = JSON.stringify(watch('saleBranchIds') || []);
  useEffect(() => {
    if (sameAsSale) {
      setValue('useBranchIds', JSON.parse(sameAsSaleSourceKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sameAsSale, sameAsSaleSourceKey]);

  // 할인 단위 메뉴 상태
  const [discountUnitAnchor, setDiscountUnitAnchor] = useState(null);

  // 이용기간 단위 메뉴 상태
  const [usagePeriodUnitAnchor, setUsagePeriodUnitAnchor] = useState(null);

  // 운동종목 옵션 변환 (i18n으로 한글 라벨 적용)
  const sportOptions =
    centrerInfo?.branchDetailInfo?.sportTypes?.map(type => ({
      label: t(type),
      value: type,
    })) || [];

  // 선택된 운동종목 (배열로 관리)
  const selectedSports = watch('sports') ? (Array.isArray(watch('sports')) ? watch('sports') : [watch('sports')]) : [];

  // 운동종목 선택 변경 — 빈 배열은 기존 규약대로 '' 로 저장한다
  const handleSportsChange = nextSports => {
    setValue('sports', nextSports.length > 0 ? nextSports : '');
  };

  // 필드 값 변경 시 해당 에러 제거
  const watchedFields = watch(['sports', 'passName', 'usagePeriod', 'regularPrice']);
  useEffect(() => {
    const [sports, passName, usagePeriod, regularPrice] = watchedFields;
    setErrorFields(prev => {
      const updated = { ...prev };
      const hasSports = Array.isArray(sports) ? sports.length > 0 : !!sports;
      if (hasSports) delete updated.sports;
      if (passName) delete updated.passName;
      if (usagePeriod) delete updated.usagePeriod;
      if (regularPrice) delete updated.regularPrice;
      return updated;
    });
  }, [watchedFields[0], watchedFields[1], watchedFields[2], watchedFields[3]]);

  // 유효성 검사
  const validateForm = () => {
    const values = watch();
    const errors = {};

    if (!values.passName) errors.passName = true;
    if (selectedSports.length === 0) errors.sports = true;
    // 구독제는 이용기간이 1개월로 고정되므로 이용기간 검사를 건너뛴다.
    if (productType !== 'SUBSCRIPTION' && !values.usagePeriod) errors.usagePeriod = true;
    if (!values.regularPrice) errors.regularPrice = true;

    setErrorFields(errors);

    if (Object.keys(errors).length > 0) {
      const firstError = Object.keys(errors)[0];
      const errorMessages = {
        passName: '이용권명을 입력해주세요',
        sports: '운동종목을 선택해주세요',
        usagePeriod: '이용기간을 입력해주세요',
        regularPrice: '판매금액을 입력해주세요',
      };
      toast.error(t(errorMessages[firstError]));
      return false;
    }
    return true;
  };

  // 다음 버튼 핸들러
  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <>
      <CardsContainer>
        {/* 기본정보 + 구독설정을 한 칼럼으로 세로 정렬 */}
        <BasicInfoColumn>
          {/* Card 1: 기본정보 */}
          <InfoCard>
            <InfoCardHeader>
              <InfoCardTitle>기본정보</InfoCardTitle>
              {isEditMode && (
                <ToggleContainer>
                  <ToggleLabel>앱 노출</ToggleLabel>
                  <StyledSwitch checked={appExposure} onChange={onExposureToggle} />
                </ToggleContainer>
              )}
            </InfoCardHeader>

            {/* 구독상품 여부 */}
            <FieldGroup>
              <FieldLabel>구독상품 여부</FieldLabel>
              <RadioGroupRow sx={isEditMode ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                <RadioOption onClick={() => !isEditMode && setProductType && setProductType('GENERAL')}>
                  <RadioCircleNew checked={productType === 'GENERAL'}>
                    {productType === 'GENERAL' && <RadioDotNew />}
                  </RadioCircleNew>
                  <RadioLabelNew>일반 이용권</RadioLabelNew>
                </RadioOption>
                <RadioOption onClick={() => !isEditMode && setProductType && setProductType('SUBSCRIPTION')}>
                  <RadioCircleNew checked={productType === 'SUBSCRIPTION'}>
                    {productType === 'SUBSCRIPTION' && <RadioDotNew />}
                  </RadioCircleNew>
                  <RadioLabelNew>구독제 이용권</RadioLabelNew>
                </RadioOption>
              </RadioGroupRow>
            </FieldGroup>

            {/* 이용권명 */}
            <FieldGroup>
              <FieldLabel>이용권명</FieldLabel>
              <ErrorFieldWrapper hasError={errorFields.passName} fullWidth>
                <FormInputText
                  control={control}
                  rules={{ required: true }}
                  name="passName"
                  placeholder={copySourceName ? `${copySourceName} (원본 이름)` : '이용권명을 입력해 주세요.'}
                />
              </ErrorFieldWrapper>
            </FieldGroup>

            {/* 판매지점·이용지점 (#73 판매/이용 분리 — 직영점 2곳 이상일 때만 다중 선택, 모달에서 체크박스로 선택) */}
            {isMultiBranch ? (
              <>
                <FieldGroup>
                  <FieldLabelWithInfo>
                    <FieldLabel>판매지점</FieldLabel>
                    <Tooltip
                      title={
                        <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>이 상품을 판매(등록)할 수 있는 지점입니다. 매출은 실제 결제가 등록된 지점에 귀속됩니다.</Box>
                      }
                      arrow
                      placement="top">
                      <InfoIconSmall>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                          <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                            ?
                          </text>
                        </svg>
                      </InfoIconSmall>
                    </Tooltip>
                  </FieldLabelWithInfo>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 14px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        backgroundColor: '#F7F7F7',
                        fontSize: '14px',
                        color: '#333',
                      }}>
                      {branchSummaryText('saleBranchIds') || (
                        <SportTagPlaceholder>이 상품을 판매할 지점을 선택해 주세요.</SportTagPlaceholder>
                      )}
                    </Box>
                    <Box
                      onClick={() => openBranchModal('saleBranchIds')}
                      sx={{
                        flexShrink: 0,
                        padding: '0 14px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                        border: '1px solid #4AB3BC',
                        color: '#4AB3BC',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        '&:hover': { backgroundColor: '#EAF7F8' },
                      }}>
                      지점 선택
                    </Box>
                  </Box>
                </FieldGroup>
                <FieldGroup>
                  <FieldLabelWithInfo>
                    <FieldLabel>이용지점</FieldLabel>
                    <Tooltip
                      title={
                        <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>구매한 회원이 실제로 출입·예약 등 이용할 수 있는 지점입니다. 판매지점과 달라도 됩니다. '구매한 지점만 이용'을 켜면 지점 목록 대신 회원이 결제한 지점 1곳에서만 이용합니다.</Box>
                      }
                      arrow
                      placement="top">
                      <InfoIconSmall>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                          <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                            ?
                          </text>
                        </svg>
                      </InfoIconSmall>
                    </Tooltip>
                  </FieldLabelWithInfo>
                  <CheckboxRow sx={{ marginBottom: '8px' }}>
                    <CheckboxNew checked={sameAsSale} onClick={toggleSameAsSale}>
                      {sameAsSale && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                          <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </CheckboxNew>
                    <CheckboxLabelNew>판매지점과 동일</CheckboxLabelNew>
                    {/* 소속지점 체크(#98)는 '판매지점과 동일' 옆에 나란히 */}
                    <Box sx={{ width: '20px', flexShrink: 0 }} />
                    <CheckboxNew checked={useSaleBranch} onClick={toggleUseSaleBranch}>
                      {useSaleBranch && (
                        <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                          <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </CheckboxNew>
                    <CheckboxLabelNew>구매한 지점만 이용 (소속지점)</CheckboxLabelNew>
                  </CheckboxRow>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', opacity: sameAsSale ? 0.55 : 1 }}>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 14px',
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px',
                        backgroundColor: '#F7F7F7',
                        fontSize: '14px',
                        color: '#333',
                      }}>
                      {useSaleBranch
                        ? '구매한 지점 (회원이 결제한 지점에서만 이용)'
                        : branchSummaryText('useBranchIds') || (
                            <SportTagPlaceholder>구매 회원이 이용 가능한 지점을 선택해 주세요.</SportTagPlaceholder>
                          )}
                    </Box>
                    <Box
                      onClick={() => !useBranchLocked && openBranchModal('useBranchIds')}
                      sx={{
                        flexShrink: 0,
                        padding: '0 14px',
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: '8px',
                        border: '1px solid #4AB3BC',
                        color: '#4AB3BC',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: useBranchLocked ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        pointerEvents: useBranchLocked ? 'none' : 'auto',
                        '&:hover': { backgroundColor: '#EAF7F8' },
                      }}>
                      지점 선택
                    </Box>
                  </Box>
                </FieldGroup>
                <BranchSelectModal
                  open={branchModalOpen}
                  title={branchMenuField === 'saleBranchIds' ? '판매지점 선택' : '이용지점 선택'}
                  branches={branchOptions}
                  selectedIds={branchFieldValue(branchMenuField)}
                  onChangeIds={ids => setValue(branchMenuField, ids)}
                  onClose={() => setBranchModalOpen(false)}
                />
              </>
            ) : (
              <FieldGroup>
                <FieldLabel>판매지점</FieldLabel>
                <FormInputDropdown
                  name="branchId"
                  control={control}
                  disabled
                  staticValue={user?.branch?.branchId}
                  options={[{ label: user?.branch?.branchName || '지점 선택', value: user?.branch?.branchId }]}
                  placeholder="판매지점을 선택해 주세요."
                />
              </FieldGroup>
            )}

            {/* 운동종목 */}
            <FieldGroup>
              <FieldLabel>운동종목</FieldLabel>
              <SportTypeSelect
                options={sportOptions}
                value={selectedSports}
                onChange={handleSportsChange}
                hasError={errorFields.sports}
              />
            </FieldGroup>
          </InfoCard>

          {/* Card: 출입설정 — 전환 지점에서만 노출. 미제한 문은 선택 고정 표시 */}
          <AccessDoorSelect
            branchId={user?.branch?.branchId}
            token={user?.token}
            selectedDoorIds={selectedDoorIds}
            onChange={onDoorIdsChange}
            onConvertedChange={onDoorConvertedChange}
            wrapper={content => (
              <InfoCard>
                <InfoCardTitle>출입설정</InfoCardTitle>
                {content}
              </InfoCard>
            )}
          />
        </BasicInfoColumn>

        {/* Card 2: 이용설정 */}
        <InfoCard>
          <InfoCardHeaderRow>
            <InfoCardTitle>이용설정</InfoCardTitle>
            <InfoCardHint>*미입력시 기본값은 '무제한'으로 적용됩니다.</InfoCardHint>
          </InfoCardHeaderRow>

          <FieldGroup>
            <FieldLabel>이용구분</FieldLabel>
            <RadioGroupRow>
              <RadioOption onClick={() => setUsageType && setUsageType('PERIOD')}>
                <RadioCircleNew checked={usageType === 'PERIOD'}>
                  {usageType === 'PERIOD' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>기간제</RadioLabelNew>
              </RadioOption>
              <RadioOption onClick={() => setUsageType && setUsageType('COUNT')}>
                <RadioCircleNew checked={usageType === 'COUNT'}>
                  {usageType === 'COUNT' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>횟수제</RadioLabelNew>
              </RadioOption>
            </RadioGroupRow>
          </FieldGroup>
          {/* 이용기간 — 구독제는 월간 자동결제라 1개월 고정(회색 블라인드) */}
          <HalfWidthContainer>
            <HalfWidthField>
              <FieldGroup>
                <FieldLabel>이용기간</FieldLabel>
                {productType === 'SUBSCRIPTION' ? (
                  <UnitInputCombo blind>
                    <UnitInputField value="1" type="number" readOnly disabled />
                    <UnitFixedText>개월</UnitFixedText>
                  </UnitInputCombo>
                ) : (
                  <UnitInputCombo hasError={errorFields.usagePeriod}>
                    <Controller
                      control={control}
                      name="usagePeriod"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <UnitInputField {...field} value={field.value ?? ''} type="number" placeholder="0" />
                      )}
                    />
                    <UnitDropdownButton onClick={e => setUsagePeriodUnitAnchor(e.currentTarget)}>
                      {usagePeriodType === 'MONTH' ? '개월' : '일'}
                      <KeyboardArrowDownIcon />
                    </UnitDropdownButton>
                    <Menu
                      anchorEl={usagePeriodUnitAnchor}
                      open={Boolean(usagePeriodUnitAnchor)}
                      onClose={() => setUsagePeriodUnitAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      <MenuItem
                        onClick={() => {
                          setUsagePeriodType('MONTH');
                          setUsagePeriodUnitAnchor(null);
                        }}>
                        개월
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setUsagePeriodType('DAY');
                          setUsagePeriodUnitAnchor(null);
                        }}>
                        일
                      </MenuItem>
                    </Menu>
                  </UnitInputCombo>
                )}
              </FieldGroup>
            </HalfWidthField>
            <HalfWidthField>
              {productType === 'GENERAL' && usageType === 'COUNT' && (
                <FieldGroup>
                  <FieldLabel>이용횟수</FieldLabel>
                  <UnitInputCombo>
                    <Controller
                      control={control}
                      name="usageCount"
                      render={({ field }) => (
                        <UnitInputField {...field} value={field.value ?? ''} type="number" placeholder="0" />
                      )}
                    />
                    <UnitFixedText>회</UnitFixedText>
                  </UnitInputCombo>
                </FieldGroup>
              )}
            </HalfWidthField>
          </HalfWidthContainer>

          {/* 중지권 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabelWithInfo>
                <FieldLabel>중지권</FieldLabel>
                <Tooltip
                  title={
                    <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
                      무제한: 중지 횟수·기간 제한 없음
                      <br />
                      중지 불가: 회원이 중지 신청 불가
                      <br />
                      직접 입력: 허용할 중지 횟수와 기간 지정
                    </Box>
                  }
                  arrow
                  placement="top">
                  <InfoIconSmall>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <circle cx="7" cy="7" r="6.5" stroke="#A6A6A6" />
                      <text x="7" y="11" textAnchor="middle" fontSize="10" fill="#A6A6A6">
                        ?
                      </text>
                    </svg>
                  </InfoIconSmall>
                </Tooltip>
              </FieldLabelWithInfo>
            </FieldLabelRow>
            <RadioGroupRow>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'UNLIMITED');
                  setValue('stopCount', '');
                  setValue('stopPeriod', '');
                }}>
                <RadioCircleNew checked={watch('stopType') === 'UNLIMITED'}>
                  {watch('stopType') === 'UNLIMITED' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>무제한</RadioLabelNew>
              </RadioOption>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'DISABLED');
                  setValue('stopCount', 0);
                  setValue('stopPeriod', 0);
                }}>
                <RadioCircleNew checked={watch('stopType') === 'DISABLED'}>
                  {watch('stopType') === 'DISABLED' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>중지 불가</RadioLabelNew>
              </RadioOption>
              <RadioOption
                onClick={() => {
                  setValue('stopType', 'CUSTOM');
                  if (!watch('stopCount')) setValue('stopCount', 1);
                  if (!watch('stopPeriod')) setValue('stopPeriod', 30);
                }}>
                <RadioCircleNew checked={watch('stopType') === 'CUSTOM'}>
                  {watch('stopType') === 'CUSTOM' && <RadioDotNew />}
                </RadioCircleNew>
                <RadioLabelNew>직접 입력</RadioLabelNew>
              </RadioOption>
            </RadioGroupRow>
            <HalfWidthContainer>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="stopCount"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('stopType') !== 'CUSTOM',
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>회</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
              <HalfWidthField>
                <FormInputText
                  control={control}
                  name="stopPeriod"
                  placeholder="0"
                  InputProps={{
                    readOnly: watch('stopType') !== 'CUSTOM',
                    endAdornment: (
                      <InputAdornment position="end">
                        <InputUnit sx={{ marginRight: 0 }}>일</InputUnit>
                      </InputAdornment>
                    ),
                  }}
                />
              </HalfWidthField>
            </HalfWidthContainer>
          </FieldGroup>

          {/* 요일설정 */}
          <FieldGroup>
            <FieldLabel>요일설정</FieldLabel>
            <DayButtonsRow>
              {DAYS.map(day => (
                <DayButtonNew
                  key={day.value}
                  selected={selectedDays.includes(day.value)}
                  onClick={() => handleDayToggle(day.value)}>
                  {day.label}
                </DayButtonNew>
              ))}
            </DayButtonsRow>
          </FieldGroup>

          {/* 시간대설정 */}
          <FieldGroup>
            <FieldLabel>시간대설정</FieldLabel>
            <TimePickerContainer>
              <TimePickerGroup>
                <TimeSelectSmall
                  value={timeSettings.startHour}
                  onChange={e => handleTimeChange('startHour', e.target.value)}
                  IconComponent={KeyboardArrowDownIcon}>
                  {HOURS.map(h => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </TimeSelectSmall>
                <TimeColon>:</TimeColon>
                <TimeSelectSmall
                  value={timeSettings.startMinute}
                  onChange={e => handleTimeChange('startMinute', e.target.value)}
                  IconComponent={KeyboardArrowDownIcon}>
                  {MINUTES.map(m => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TimeSelectSmall>
              </TimePickerGroup>
              <TimeTilde>~</TimeTilde>
              <TimePickerGroup>
                <TimeSelectSmall
                  value={timeSettings.endHour}
                  onChange={e => handleTimeChange('endHour', e.target.value)}
                  IconComponent={KeyboardArrowDownIcon}>
                  {HOURS.map(h => (
                    <MenuItem key={h} value={h}>
                      {h}
                    </MenuItem>
                  ))}
                </TimeSelectSmall>
                <TimeColon>:</TimeColon>
                <TimeSelectSmall
                  value={timeSettings.endMinute}
                  onChange={e => handleTimeChange('endMinute', e.target.value)}
                  IconComponent={KeyboardArrowDownIcon}>
                  {MINUTES.map(m => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TimeSelectSmall>
              </TimePickerGroup>
            </TimePickerContainer>
          </FieldGroup>
        </InfoCard>

        {/* Card 3: 금액설정 */}
        <InfoCard>
          <InfoCardTitle>금액설정</InfoCardTitle>

          {/* 판매금액 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>판매금액</FieldLabel>
              {/* <CheckboxRow>
                <CheckboxNew checked={isTaxFree} onClick={() => setIsTaxFree(!isTaxFree)}>
                  {isTaxFree && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>비과세 여부</CheckboxLabelNew>
              </CheckboxRow> */}
            </FieldLabelRow>
            <ErrorFieldWrapper hasError={errorFields.regularPrice} fullWidth>
              <FormInputPrice
                control={control}
                rules={{ required: true }}
                name="regularPrice"
                placeholder="0"
                type="tel"
                onChange={handleRegularPriceChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InputUnit sx={{ marginRight: 0 }}>원</InputUnit>
                    </InputAdornment>
                  ),
                }}
              />
            </ErrorFieldWrapper>
          </FieldGroup>

          {/* 할인금액 */}
          <FieldGroup>
            <FieldLabel>할인금액</FieldLabel>
            <FormInputPrice
              control={control}
              name="discountRate"
              placeholder="0"
              onChange={handleDiscountRateChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <DiscountUnitSelect onClick={e => setDiscountUnitAnchor(e.currentTarget)}>
                      <span className="unit-text">{discountType}</span>
                      <span className="unit-arrow">
                        <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                      </span>
                    </DiscountUnitSelect>
                    <Menu
                      anchorEl={discountUnitAnchor}
                      open={Boolean(discountUnitAnchor)}
                      onClose={() => setDiscountUnitAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      <MenuItem
                        onClick={() => {
                          setDiscountType('원');
                          setDiscountUnitAnchor(null);
                        }}>
                        원
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setDiscountType('%');
                          setDiscountUnitAnchor(null);
                        }}>
                        %
                      </MenuItem>
                    </Menu>
                  </InputAdornment>
                ),
              }}
            />
          </FieldGroup>

          {/* 결제금액 */}
          <FieldGroup>
            <FieldLabel>결제금액</FieldLabel>
            <FormInputPrice
              control={control}
              name="finalPrice"
              placeholder="0"
              onChange={handleFinalPriceChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <InputUnit sx={{ marginRight: 0 }}>원</InputUnit>
                  </InputAdornment>
                ),
              }}
            />
          </FieldGroup>
        </InfoCard>

        {/* Card 4: 기간설정 */}
        <InfoCard>
          <InfoCardTitle>기간설정</InfoCardTitle>

          {/* 판매기간 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>판매기간</FieldLabel>
              <CheckboxRow>
                <CheckboxNew
                  checked={watch('hasSaleEndDate')}
                  onClick={() => setValue('hasSaleEndDate', !watch('hasSaleEndDate'))}>
                  {watch('hasSaleEndDate') && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>종료일 없음</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <DatePickerRow>
              <DatePickerField>
                <FormInputDate name="saleStartDate" control={control} placeholder="시작일" />
              </DatePickerField>
              <TimeTilde>~</TimeTilde>
              <DatePickerField>
                <FormInputDate
                  name="saleEndDate"
                  control={control}
                  disabled={watch('hasSaleEndDate')}
                  minDate={watch('saleStartDate')}
                  placeholder="종료일"
                />
              </DatePickerField>
            </DatePickerRow>
          </FieldGroup>

          {/* 할인기간 */}
          <FieldGroup>
            <FieldLabelRow>
              <FieldLabel>할인기간</FieldLabel>
              <CheckboxRow>
                <CheckboxNew checked={hasDiscountEndDate} onClick={() => setHasDiscountEndDate(!hasDiscountEndDate)}>
                  {hasDiscountEndDate && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </CheckboxNew>
                <CheckboxLabelNew>종료일 없음</CheckboxLabelNew>
              </CheckboxRow>
            </FieldLabelRow>
            <DatePickerRow>
              <DatePickerField>
                <FormInputDate name="discountStartDate" control={control} placeholder="시작일" />
              </DatePickerField>
              <TimeTilde>~</TimeTilde>
              <DatePickerField>
                <FormInputDate
                  name="discountEndDate"
                  control={control}
                  disabled={hasDiscountEndDate}
                  placeholder="종료일"
                />
              </DatePickerField>
            </DatePickerRow>
          </FieldGroup>
        </InfoCard>
      </CardsContainer>

      {/* Bottom Buttons */}
      <BottomActionsNew>
        <CancelButtonNew onClick={onCancel}>취소</CancelButtonNew>
        <NextButtonNew onClick={handleNext} disabled={loader}>
          다음
        </NextButtonNew>
      </BottomActionsNew>
    </>
  );
}

export default PassEditStep01;
